import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import UniversalScoreBadge from './UniversalScoreBadge';
import WhyBullets from './WhyBullets';
import FreshnessBadge from '../CreditCardComparison/FreshnessBadge';
import TrustMicroline from './TrustMicroline';
import { computeFreshness, toFreshnessBadgeStatus, formatMonthYear } from './freshness';
import { trackClickApply } from './analytics';
import { getLogoPath } from './logoMap';

/**
 * Full detail dialog for any product vertical.
 * Reads config.detailSections(item) for dynamic section rendering.
 */
const ProductDetailModal = ({ open, onClose, item, config, rank, category, onExplainerClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!item) return null;

  const name = item[config.nameField] || '';
  const institution = item[config.institutionField] || '';
  const logoName = config.logoField ? (item[config.logoField] || '') : '';
  const logoPath = getLogoPath(institution) || getLogoPath(name) || getLogoPath(logoName);
  const sections = config.detailSections?.(item) || [];
  const applyUrl = config.getApplyUrl?.(item);
  const officialUrl = config.getOfficialUrl?.(item);

  // Computed freshness (60/120 day thresholds)
  const freshnessInfo = computeFreshness(item.data_ops?.last_verified_at);
  const badgeStatus = item.data_ops ? toFreshnessBadgeStatus(freshnessInfo.status) : null;

  // Data sources
  const sources = item.data_ops?.sources || [];
  const verificationMethod = item.data_ops?.verification_method;

  const handleCTAClick = (url) => {
    if (category && item.id) {
      trackClickApply(category, String(item.id), url);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: isMobile ? 0 : '14px' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, flex: 1, mr: 2 }}>
          {logoPath && (
            <Box
              sx={{
                width: 72,
                height: 40,
                borderRadius: '8px',
                bgcolor: 'neutral.50',
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                overflow: 'hidden',
                p: 0.5,
              }}
            >
              <Box
                component="img"
                src={logoPath}
                alt={institution}
                width={64}
                height={32}
                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {institution}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <UniversalScoreBadge item={item} onExplainerClick={onExplainerClick} />
            {badgeStatus && <FreshnessBadge status={badgeStatus} />}
          </Box>
          {/* Bonus headline */}
          {item.welcome_bonus?.active && item.welcome_bonus.value_cad > 0 && (
            <Chip
              label={`Bono: $${item.welcome_bonus.value_cad.toLocaleString('en-CA')} CAD`}
              size="small"
              sx={{ mt: 1, fontWeight: 600, bgcolor: 'action.hover', color: 'secondary.main' }}
            />
          )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Cerrar detalle">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2.5 }}>
        {/* Summary box */}
        {item.content?.short_description_es && (
          <Box sx={{ bgcolor: 'neutral.50', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5, mb: 3.5 }}>
            <Typography sx={{ fontSize: '0.9375rem', color: 'text.primary', lineHeight: 1.7, fontWeight: 500 }}>
              {item.content.short_description_es}
            </Typography>
          </Box>
        )}

        {/* Why bullets */}
        <Box sx={{ mb: 3 }}>
          <WhyBullets item={item} rank={rank} />
        </Box>

        {/* Dynamic sections */}
        {sections.map((sec, si) => (
          <Box key={si} sx={{ bgcolor: 'neutral.25', borderRadius: 2, p: 2.5, mb: 2.5 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5 }}
            >
              {sec.title}
            </Typography>
            {sec.rows && sec.rows.length > 0 ? sec.rows.map((row, ri) => (
              <Box
                key={ri}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  py: 0.75,
                  borderBottom: ri < sec.rows.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mr: 2, flexShrink: 0 }}>
                  {row.label}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>
                  {row.value}
                </Typography>
              </Box>
            )) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>-</Typography>
            )}
          </Box>
        ))}

        {/* Registered accounts: providers table */}
        {item.best_providers_2026 && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              Mejores proveedores 2026
            </Typography>
            {item.best_providers_2026.map((p, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 0.75,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {p.provider}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {p.rate_percent > 0 ? `${p.rate_percent}%` : ''} {p.notes}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Can hold (registered accounts) */}
        {item.can_hold && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              Puede contener
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {item.can_hold.map((h, i) => (
                <Chip key={i} label={h} size="small" sx={{ fontSize: '0.75rem' }} />
              ))}
            </Box>
          </Box>
        )}

        {/* ── Data Sources Section ── */}
        {item.data_ops && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                Fuentes de datos
              </Typography>

              {/* Last verified + method */}
              {item.data_ops.last_verified_at && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                  Última verificación: {formatMonthYear(item.data_ops.last_verified_at)}
                  {verificationMethod && ` · Método: ${verificationMethod}`}
                </Typography>
              )}

              {/* Source list */}
              {sources.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {sources.map((src, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 0.5,
                        borderBottom: i < sources.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                          {src.title || src.type}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                          {src.type}
                          {src.retrieved_at && ` · ${formatMonthYear(src.retrieved_at)}`}
                        </Typography>
                      </Box>
                      {src.url && (
                        <IconButton
                          component="a"
                          href={src.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          aria-label="Abrir fuente"
                          sx={{ color: 'text.disabled', ml: 1 }}
                        >
                          <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* CTAs */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 3 }}>
          {applyUrl && (
            <Button
              variant="contained"
              href={applyUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleCTAClick(applyUrl)}
              sx={{
                bgcolor: 'secondary.main',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                flex: 1,
                py: 1.5,
                '&:hover': { bgcolor: 'secondary.dark' },
              }}
            >
              Aplicar ahora
            </Button>
          )}
          {officialUrl && officialUrl !== applyUrl && (
            <Button
              variant="outlined"
              href={officialUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleCTAClick(officialUrl)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                flex: 1,
                py: 1.5,
              }}
            >
              Sitio oficial
            </Button>
          )}
        </Box>

        {/* Trust microline */}
        {(applyUrl || officialUrl) && <TrustMicroline />}
      </DialogContent>
    </Dialog>
  );
};

export default ProductDetailModal;
